import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2, AlertCircle, ArrowLeft, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PhoenixWordmark } from "@/components/PhoenixLogo";
import { TIDS } from "@/constants/testIds";

export default function StaffLoginPage() {
    const { login, verifyMfa } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [mfaStep, setMfaStep] = useState(false);
    const [challengeToken, setChallengeToken] = useState("");
    const [mfaCode, setMfaCode] = useState("");

    const goAfterLogin = (u) => {
        const to = location.state?.from?.pathname ||
            (u.role === "inmate" ? "/inmate" : u.role === "provider" ? "/provider" : u.role === "admin" ? "/admin" : "/institution");
        navigate(to, { replace: true });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const res = await login({ email: email.trim(), password });
            if (res.mfaRequired) {
                setChallengeToken(res.challengeToken);
                setMfaStep(true);
            } else {
                goAfterLogin(res.user);
            }
        } catch (e) {
            setError(e?.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    const handleMfaSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const u = await verifyMfa(challengeToken, mfaCode.trim());
            goAfterLogin(u);
        } catch (e) {
            setError(e?.response?.data?.detail || "Invalid code");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-phoenix-mesh flex flex-col">
            <header className="max-w-6xl mx-auto w-full px-6 py-6 flex items-center justify-between">
                <PhoenixWordmark />
                <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-phoenix-navy transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" /> Back
                </Link>
            </header>

            <div className="flex-1 flex items-center justify-center px-6 py-10">
                <div className="w-full max-w-md">
                    <div className="text-center mb-7 animate-fade-up">
                        <h1 className="font-display font-bold text-3xl text-phoenix-navy mb-2">
                            {mfaStep ? "Two-factor verification" : "Welcome back"}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {mfaStep ? "Enter the 6-digit code from your authenticator app" : "Sign in to your facility staff dashboard"}
                        </p>
                    </div>

                    {mfaStep ? (
                        <form
                            onSubmit={handleMfaSubmit}
                            data-testid="staff-mfa-form"
                            className="bg-white p-7 rounded-3xl shadow-phoenix-card border border-border/60 space-y-5 animate-fade-up"
                            style={{ animationDelay: "60ms" }}
                        >
                            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-phoenix-soft/60 rounded-2xl">
                                <ShieldCheck className="w-6 h-6 text-phoenix-navy" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-2">
                                    Authentication code
                                </label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoFocus
                                    value={mfaCode}
                                    onChange={(e) => setMfaCode(e.target.value)}
                                    required
                                    maxLength={20}
                                    data-testid="staff-mfa-code"
                                    className="w-full px-4 py-3 border border-border rounded-xl text-center text-lg tracking-[0.4em] font-mono focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30 focus:border-phoenix-navy transition-all"
                                    placeholder="000000"
                                />
                                <p className="text-xs text-muted-foreground mt-1.5 text-center">Lost your device? Enter one of your backup codes instead.</p>
                            </div>

                            {error && (
                                <div data-testid="staff-mfa-error" className="flex items-start gap-2 text-sm text-destructive bg-destructive/5 border border-destructive/15 rounded-xl px-3.5 py-2.5">
                                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="staff-mfa-submit"
                                className="w-full py-3 bg-phoenix-navy text-white font-semibold rounded-xl hover:bg-phoenix-navy-2 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                            >
                                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                                Verify & sign in
                            </button>
                            <button
                                type="button"
                                onClick={() => { setMfaStep(false); setMfaCode(""); setError(""); }}
                                className="w-full text-center text-sm text-muted-foreground hover:text-phoenix-navy"
                            >
                                ← Back to sign in
                            </button>
                        </form>
                    ) : (
                    <form
                        onSubmit={handleSubmit}
                        data-testid={TIDS.staffLoginForm}
                        className="bg-white p-7 rounded-3xl shadow-phoenix-card border border-border/60 space-y-5 animate-fade-up"
                        style={{ animationDelay: "60ms" }}
                    >
                        <div>
                            <label className="block text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-2">
                                Email
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                data-testid={TIDS.staffLoginEmail}
                                className="w-full px-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30 focus:border-phoenix-navy transition-all"
                                placeholder="you@yourfacility.org"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-2">
                                Password
                            </label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                                data-testid={TIDS.staffLoginPassword}
                                className="w-full px-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30 focus:border-phoenix-navy transition-all"
                                placeholder="••••••••"
                            />
                        </div>

                        {error && (
                            <div
                                data-testid={TIDS.staffLoginError}
                                className="flex items-start gap-2 text-sm text-destructive bg-destructive/5 border border-destructive/15 rounded-xl px-3.5 py-2.5"
                            >
                                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid={TIDS.staffLoginSubmit}
                            className="w-full py-3 bg-phoenix-navy text-white font-semibold rounded-xl hover:bg-phoenix-navy-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                            Sign in
                        </button>

                        <div className="text-center text-sm pt-2 border-t border-border/60">
                            <span className="text-muted-foreground">Are you an inmate? </span>
                            <Link to="/claim-access" className="text-phoenix-navy font-semibold hover:text-phoenix-orange transition-colors">
                                Use your access code
                            </Link>
                        </div>
                    </form>
                    )}
                </div>
            </div>
        </div>
    );
}
